import { FormEvent, useEffect, useState } from 'react'
import Validator from 'validator'

import LogoSVG from '../../assets/svgs/Logo.svg'
import { AppButton } from '../atoms/AppButton/AppButton'
import AppTooltip from '../atoms/AppTooltip'
import TextLine from '../atoms/Forms/TextLine'
import { useSelector } from 'react-redux'
import { AuthState, login } from '../../slices/auth-slice'
import { isFailure, isLoading } from '../../types/definitions/app-state'
import { RootState, useAppDispatch } from '../..'
import { AppInputPassword } from '../atoms/AppInput/AppInputPassword'
import WarningIconFilled from '../atoms/Icons/WarningIconFilled.svg'
import { classNames } from 'primereact/utils'
import { ConfigService } from '../../services/ConfigService'
import OptionsModal from '../molecules/OptionsModal'

const LoginPage = (): JSX.Element => {
  const CONFIG_FIELD_SSO_INIT_URI = 'ssoInitializerUri'
  const CONFIG_FIELD_SSO_LOGIN_ENABLE = 'SSOLoginEnable'
  const EMAIL = 'mailto:grapes@theagilemonkeys.com'

  const state = useSelector<RootState, AuthState>(state => state.auth)
  const dispatch = useAppDispatch()

  const [emailValidation, setEmailValidation] = useState<boolean>(true)
  const [passwordValidation, setPasswordValidation] = useState<boolean>(true)
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [helperText, setHelperText] = useState<string>('')
  const [isEmailError, setIsEmailError] = useState<boolean>(false)
  const [isPasswordError, setIsPasswordError] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [ssoInitUri, setSsoInitUri] = useState<string>('')
  const [loadingSSO, setLoadingSSO] = useState<boolean>(false)
  const [ssoLoginEnabled, setSsoLoginEnabled] = useState<boolean>(false)
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState<boolean>(false)

  useEffect(() => {
    setLoading(isLoading(state.status))
    const failure = isFailure(state.status)
    setIsEmailError(failure)
    setIsPasswordError(failure)
    if (failure) {
      setHelperText('Incorrect email address or password')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  useEffect(() => {
    // TODO: Improve this error management
    if (!email) {
      setIsEmailError(false)
    }
    if (!password) {
      setIsPasswordError(false)
    }
    if (!email || !password) {
      setHelperText('')
    }
  }, [email, password])

  useEffect(() => {
    ConfigService.getConfig([CONFIG_FIELD_SSO_INIT_URI, CONFIG_FIELD_SSO_LOGIN_ENABLE]).then(response => {
      setSsoInitUri((response.data[CONFIG_FIELD_SSO_INIT_URI] as string | undefined) ?? '')
      setSsoLoginEnabled(Boolean(response.data[CONFIG_FIELD_SSO_LOGIN_ENABLE]))
    })
  }, [])

  const handleSignIn = (event: FormEvent): void => {
    event.preventDefault()
    if (emailValidation && passwordValidation) {
      dispatch(login({ email, password }))
    }
  }

  const checkEmail = (): void => {
    if (!Validator.isEmail(email)) {
      setIsEmailError(true)
    }
  }

  const initSSO = (): void => {
    setLoadingSSO(true)
    window.location.href = ssoInitUri
  }

  const handleSendEmail = (): void => {
    setShowForgotPasswordModal(false)
    window.open(
      `${EMAIL}?subject=${encodeURIComponent('Password reset request')}&body=${encodeURIComponent(
        'Hi Grapes team, I forgot my password. Please reset it when possible. Thanks.'
      )}`
    )
  }

  return (
    <div
      className={classNames({
        'flex-1 container flex justify-center bg-white': true,
        'animate-pulse': loadingSSO,
      })}
    >
      <div className="flex flex-col justify-center w-176">
        <div className="w-full">
          <h1 hidden>Grapes | Your assessment tool</h1>
          <div className="pb-8 mb-12">
            <LogoSVG />
          </div>
          <p className="mb-8 lg:mb-16 text-5xl text-normal text-dark-blue">Talent Assessments</p>
        </div>
        {ssoLoginEnabled ? (
          <AppButton className="w-full" onClick={initSSO} disabled={loadingSSO}>
            Log in with Enterprise Secure Sign On
          </AppButton>
        ) : (
          <AppTooltip
            title="This feature will be available soon."
            placement="top"
            arrow
            classes={{ tooltip: 'dot-tooltip', arrow: 'dot-tooltip-arrow' }}
          >
            <span className="inline-block w-full">
              <AppButton className="w-full" onClick={initSSO} disabled>
                Log in with Enterprise Secure Sign On
              </AppButton>
            </span>
          </AppTooltip>
        )}
        <div className="flex flex-col">
          <div className="flex items-center justify-center py-8">
            <div className="w-[67px] h-[1px] bg-toast-hover "></div>
            <div className="text-2xl text-toast-hover ml-3 mr-3">or</div>
            <div className="w-[67px] h-[1px] bg-toast-hover "></div>
          </div>
        </div>
        <form onSubmit={handleSignIn}>
          <div className="flex flex-col pb-6">
            <TextLine
              className={`text-border${!isEmailError ? ' border-orange ' : ''} max-w-full mb-0 ${
                isEmailError ? ' border-red-button' : ''
              }`}
              type={'email'}
              autoComplete={'email'}
              value={email}
              onChange={value => setEmail(value)}
              setValidation={setEmailValidation}
              placeholder="Email"
            />
            <AppInputPassword
              autoComplete={'password'}
              value={password}
              onChange={value => setPassword(value)}
              setValidation={setPasswordValidation}
              placeholder="Password"
              isPasswordError={isPasswordError}
            />
            <div
              className={classNames({
                'flex items-center py-6 px-10 font-bold gap-3 w-full bg-red-button bg-opacity-10 rounded-md mt-5': true,
                'hidden ': !isPasswordError,
              })}
            >
              <WarningIconFilled />
              <p className="text-error-red text-2xl">{helperText}</p>
            </div>
          </div>
          <div className="button-container">
            <AppButton className="w-full" onClick={checkEmail} disabled={!email || !password || loading}>
              Log in
            </AppButton>
          </div>
          <div className="flex justify-center mt-4">
            <button
              type="button"
              className="text-orange underline bg-transparent border-0 p-0 text-2xl"
              onClick={() => setShowForgotPasswordModal(true)}
            >
              Forgot Password
            </button>
          </div>
        </form>
        {showForgotPasswordModal && (
          <OptionsModal
            visible={showForgotPasswordModal}
            message={
              <p className="text-[2.5rem]">
                Please send an email to{' '}
                <a className="text-orange font-semibold" href={EMAIL}>
                  grapes@theagilemonkeys.com
                </a>{' '}
                to reset your password.
              </p>
            }
            firstButtonText="Close"
            firstButtonType="clear"
            secondButtonText="Send mail"
            secondButtonType="info"
            onFirstButtonClick={() => setShowForgotPasswordModal(false)}
            onSecondButtonClick={handleSendEmail}
            onClose={() => setShowForgotPasswordModal(false)}
          />
        )}
      </div>
    </div>
  )
}
export default LoginPage
